import { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { fetchChecklist, fetchChecklistHistory, toggleChecklistItem } from 'services/thirteenWeeks';
import type { DailyChecklist, ChecklistHistoryItem } from 'types/dailyChecklist';
import type { DayStatus } from 'mock/13w.data';
import { CHECKLIST_FETCH_TIMEOUT_MS } from 'constants/api';
import { getDailyChecklist, getJournalCompletionStatus } from 'pages/Academy/ThirteenWeeks/DayDetail/function';
import type { TradingJournalResponse } from 'pages/Academy/ThirteenWeeks/DayDetail/type';

// enabled=false skips the request: GET /checklist?date= creates an empty journal for that day on the server,
// so merely previewing future days in the calendar filled the history with blank records
export function useJournalDetail(date: string, enabled = true) {
  const [data, setData] = useState<TradingJournalResponse | null>(null);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getDailyChecklist(date).then((res) => {
      if (!cancelled) {
        setData(res);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [date, enabled]);

  const completion = data ? getJournalCompletionStatus(data.journal, data.dynamic_checklists) : null;

  return { data, completion, loading };
}

function getDayStatusFromHistory(
  dateStr: string,
  today: string,
  historyMap: Record<string, ChecklistHistoryItem>,
): DayStatus {
  if (dateStr > today) return 'future';
  const record = historyMap[dateStr];
  // Today is still in progress, even before anything has been recorded
  if (!record) return dateStr === today ? 'today' : 'missed';

  // Nếu có điểm/xếp hạng cuối ngày => Hoàn thành (Done)
  if (record.eod_grade) return 'done';

  // Nếu chưa có xếp hạng nhưng đã có trade hoặc đã sẵn sàng trade => Thiếu EOD (Partial)
  if (record.total_trades > 0 || record.is_ready_to_trade) return 'partial';

  // Nếu là hôm nay và có record nhưng chưa có dữ liệu đặc biệt => Today
  if (dateStr === today) return 'today';

  return 'missed';
}

export function useDayDetail(date: string) {
  const [checklist, setChecklist] = useState<DailyChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const savingRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTimedOut(false);
    setChecklist(null);

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        setLoading(false);
        setTimedOut(true);
      }
    }, CHECKLIST_FETCH_TIMEOUT_MS);

    fetchChecklist(date).then((data) => {
      if (cancelled) return;
      clearTimeout(timeoutId);
      setChecklist(data);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      clearTimeout(timeoutId);
      setLoading(false);
      setTimedOut(true);
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [date]);

  const toggle = useCallback((itemId: number, currentValue: boolean) => {
    if (savingRef.current.has(itemId)) return;
    savingRef.current.add(itemId);

    setChecklist((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        checklist_data: prev.checklist_data.map((cat) => ({
          ...cat,
          items: cat.items.map((item) =>
            item.id === itemId ? { ...item, is_checked: !currentValue } : item
          ),
        })),
      };
    });

    toggleChecklistItem({ item_id: itemId, is_checked: !currentValue }).finally(() => {
      savingRef.current.delete(itemId);
    });
  }, []);

  const totalItems = checklist?.checklist_data.reduce((s, c) => s + c.items.length, 0) ?? 0;
  const doneCount = checklist?.checklist_data.reduce(
    (s, c) => s + c.items.filter((i) => i.is_checked).length, 0
  ) ?? 0;
  const pct = totalItems > 0 ? Math.round((doneCount / totalItems) * 100) : 0;

  return { checklist, loading, timedOut, toggle, totalItems, doneCount, pct };
}

export function useMonthChecklist(year: number, month: number) {
  const [historyMap, setHistoryMap] = useState<Record<string, ChecklistHistoryItem>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
    fetchChecklistHistory(monthStr).then((data) => {
      if (cancelled) return;
      const map: Record<string, ChecklistHistoryItem> = {};
      if (Array.isArray(data)) {
        data.forEach((item) => {
          map[item.date] = item;
        });
      }
      setHistoryMap(map);
      setLoading(false);
    }).catch(() => {
      // Without this the calendar skeleton never goes away when the request fails
      if (cancelled) return;
      setHistoryMap({});
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [year, month]);

  // Local date: toISOString() is UTC, so before 7:00 (UTC+7) "today" would still be yesterday
  const today = dayjs().format('YYYY-MM-DD');
  const getDayStatus = useCallback(
    (dateStr: string) => getDayStatusFromHistory(dateStr, today, historyMap),
    [historyMap, today],
  );

  const getDayInfo = useCallback(
    (dateStr: string): { status: DayStatus; score: number; trades: number; grade: number; isReadyToTrade: boolean } => {
      const status = getDayStatusFromHistory(dateStr, today, historyMap);
      const record = historyMap[dateStr];
      return {
        status,
        score: record?.total_eod_score ?? 0,
        trades: record?.total_trades ?? 0,
        grade: record?.eod_grade ?? 0,
        isReadyToTrade: record?.is_ready_to_trade ?? false,
      };
    },
    [historyMap, today],
  );

  return { loading, getDayStatus, getDayInfo };
}
